<template>
  <div>
    <a-row :gutter="{ xs: 8, sm: 16 }" class="mb-32px boxes">
      <a-col class="gutter-row" :xs="24" :sm="12" :md="12" :lg="8" :xl="8">
        <div class="gutter-box box-shadow-green">
          <div class="d-flex jcb aic">
            <div>
              <div class="gutter-box-number">{{ computedLastImportDate }}</div>
              <div class="gutter-box-label fs-md geomanist-font">{{ $t('label.last_autoimport') }}</div>
            </div>
            <div class="gutter-box-icon d-flex jcc aic green">
              <calendar-icon height="32" width="32" stroke="#2FC091" view-box="0 0 32 32"/>
            </div>
          </div>
        </div>
      </a-col>
      <a-col class="gutter-row" :xs="24" :sm="12" :md="12" :lg="8" :xl="8">
        <div class="gutter-box box-shadow-yellow">
          <div class="d-flex jcb aic">
            <div>
              <div class="gutter-box-number">{{ boxData.add || 0 }}</div>
              <div class="gutter-box-label fs-md geomanist-font">{{ $t('label.number_of_users_added') }}</div>
            </div>
            <div class="gutter-box-icon d-flex jcc aic yellow">
              <four-users-icon height="32" width="32" stroke="#EABA13" view-box="0 0 32 32"/>
            </div>
          </div>
        </div>
      </a-col>
      <a-col class="gutter-row" :xs="24" :sm="12" :md="12" :lg="8" :xl="8">
        <div class="gutter-box box-shadow-red">
          <div class="d-flex jcb aic">
            <div>
              <div class="gutter-box-number">{{ boxData.delete || 0 }}</div>
              <div class="gutter-box-label fs-md geomanist-font">{{ $t('label.number_of_users_deleted') }}</div>
            </div>
            <div class="gutter-box-icon d-flex jcc aic red">
              <trash-icon height="32" width="32" stroke="#FF655B" view-box="0 0 32 32"/>
            </div>
          </div>
        </div>
      </a-col>
    </a-row>

    <a-row :gutter="{ xs: 8, sm: 16, md: 24 }" class="filters-wrapper">
      <a-col :xs="24" :md="12" :lg="8" class="gutter-row mb-24px">
        <form-input
            :value="filters.search"
            :placeholder="$t('label.search')"
            allow-clear
            with-icon
            @input="filters.search = $event"
        >
          <template #prefix-icon>
            <search-icon stroke="#467BFF"/>
          </template>
        </form-input>
      </a-col>
      <!--  New element to load & filtered by input our organization list    -->
      <a-col :xs="24" :md="12" :lg="8" class="gutter-row mb-24px">
        <searchable-organization-select
            :value="filters.organization_id"
            @change="filters.organization_id = $event"
        />
      </a-col>
      <a-col :xs="24" :md="12" :lg="8" class="gutter-row mb-24px">
        <a-button class="app-button primary w-100" @click="showSetUserAsAdminModal = true">
          {{ $t('btn.set_user_as_admin') }}
        </a-button>
      </a-col>
    </a-row>

    <app-tabs :tabs="tabs" :suffix-info="tabCounts" @change="activeTab = $event">
      <template #users v-if="activeTab === 0">
        <a-alert
            v-if="showBadgesHint"
            class="mb-24px"
            type="info"
            show-icon
            :message="$t('text.no_users_but_badges', {count: formattedBadgesCount})"
        />
        <app-table
            :columns="userColumns"
            :data="usersList"
            :current="usersMeta.current_page"
            :total="usersMeta.total"
            scroll
            @getData="getTableData"
        >
          <span slot="user_id_icon">
            <user-circle-icon height="16" width="16" stroke="#467BFF"/>
            <span>{{ $t('label.user_id') }}</span>
          </span>
          <span slot="email_icon">
            <mail-icon height="16" width="16" stroke="#467BFF"/>
            <span>{{ $t('label.email') }}</span>
          </span>
          <span slot="organization_name_icon">
            <link-icon height="16" width="16" stroke="#467BFF" view-box="0 0 16 16"/>
            <span>{{ $t('label.linked_organization') }}</span>
          </span>
          <span slot="nfc_badge_id_icon">
            <identification-badge-id-icon height="16" width="16" stroke="#467BFF" view-box="0 0 16 16"/>
            <span>{{ $t('label.nfc_id') }}</span>
          </span>
          <span slot="active_pico_cycles_count_icon">
            <archive-box-download-icon height="16" width="16" stroke="#467BFF" view-box="0 0 16 16"/>
            <span>{{ $t('label.pico_taken') }}</span>
          </span>
          <span slot="status_icon">
            <four-circles-icon height="16" width="16" stroke="#467BFF" view-box="0 0 32 32"/>
            <span>{{ $t('label.status') }}</span>
          </span>

          <template #email="{ text }">{{ text || '-' }}</template>
          <template #status="{ text }">
            <a-tag :color="text?.name === 'active' ? 'green' : 'red'">{{ $t(`status.${text?.name || 'inactive'}`) }}</a-tag>
          </template>

          <template v-slot:actions="{ record }">
            <div class="d-flex table-actions-wrapper">
              <a-tooltip v-if="record.status?.name === 'active'">
                <template v-slot:title>
                  {{ $t('btn.delete_user') }}
                </template>
                <span class="cursor-pointer" @click="showDestroyUserModal(record)">
                <trash-icon height="18" width="18" stroke="#467BFF" view-box="0 0 32 32"/>
              </span>
              </a-tooltip>
              <a-tooltip v-else>
                <template v-slot:title>
                  {{ $t('btn.activate_user') }}
                </template>
                <span class="cursor-pointer" @click="showActivateUserModal(record)">
                <power-icon height="18" width="18" stroke="#467BFF" view-box="0 0 16 16"/>
              </span>
              </a-tooltip>
              <a-tooltip>
                <template v-slot:title>
                  {{ $t('btn.edit_user') }}
                </template>
                <span class="cursor-pointer" :class="{'hidden': record.status?.name !== 'active'}"
                      @click="editUser(record)">
                  <pencil-line-edit-icon height="16" width="16" stroke="#467BFF" view-box="0 0 16 16"/>
                </span>
              </a-tooltip>
              <a-tooltip>
                <template v-slot:title>
                  {{ $t('label.history') }}
                </template>
                <span class="cursor-pointer" @click="goToHistory(record)">
                  <time-reset-icon height="16" width="16" stroke="#467BFF" view-box="0 0 16 16"/>
                </span>
              </a-tooltip>
            </div>
          </template>
          <template #footer-actions>
            <a-button class="app-button outline-primary" @click="showUserModal = true">
              {{ $t('btn.create_user') }}
            </a-button>
          </template>
        </app-table>
      </template>
      <template #badges v-if="activeTab === 1">
        <badges-view
            embedded
            :organization-id="filters.organization_id"
            :search="filters.search"
        />
      </template>
      <template #admins v-if="activeTab === 2">
        <app-table
            :columns="userColumns"
            :data="adminsList"
            :current="adminsMeta.current_page"
            :total="adminsMeta.total"
            scroll
            @getData="getTableData"
        >
          <span slot="user_id_icon">
            <user-circle-icon height="16" width="16" stroke="#467BFF"/>
            <span>{{ $t('label.user_id') }}</span>
          </span>
          <span slot="email_icon">
            <mail-icon height="16" width="16" stroke="#467BFF"/>
            <span>{{ $t('label.email') }}</span>
          </span>
          <span slot="organization_name_icon">
            <link-icon height="16" width="16" stroke="#467BFF" view-box="0 0 16 16"/>
            <span>{{ $t('label.linked_organization') }}</span>
          </span>
          <span slot="nfc_badge_id_icon">
            <identification-badge-id-icon height="16" width="16" stroke="#467BFF" view-box="0 0 16 16"/>
            <span>{{ $t('label.nfc_id') }}</span>
          </span>
          <span slot="active_pico_cycles_count_icon">
            <archive-box-download-icon height="16" width="16" stroke="#467BFF" view-box="0 0 16 16"/>
            <span>{{ $t('label.pico_taken') }}</span>
          </span>
          <span slot="status_icon">
            <four-circles-icon height="16" width="16" stroke="#467BFF" view-box="0 0 32 32"/>
            <span>{{ $t('label.status') }}</span>
          </span>

          <template #email="{ text }">{{ text || '-' }}</template>
          <template #nfc_badge_id="{ text }">{{ text || '-' }}</template>
          <template #status="{ text }">
            <a-tag :color="text?.name === 'active' ? 'green' : 'red'">{{ $t(`status.${text?.name || 'inactive'}`) }}</a-tag>
          </template>

          <template v-slot:actions="{ record }">
            <div class="d-flex table-actions-wrapper">
              <a-tooltip v-if="record.status?.name === 'active'">
                <template v-slot:title>
                  {{ $t('btn.delete_user') }}
                </template>
                <span class="cursor-pointer" @click="showDestroyUserModal(record)">
                <trash-icon height="18" width="18" stroke="#467BFF" view-box="0 0 32 32"/>
              </span>
              </a-tooltip>
              <a-tooltip v-else>
                <template v-slot:title>
                  {{ $t('btn.activate_user') }}
                </template>
                <span class="cursor-pointer" @click="showActivateUserModal(record)">
                <power-icon height="18" width="18" stroke="#467BFF" view-box="0 0 16 16"/>
              </span>
              </a-tooltip>
              <a-tooltip>
                <template v-slot:title>
                  {{ $t('btn.edit_user') }}
                </template>
                <span class="cursor-pointer" :class="{'hidden': record.status?.name !== 'active'}"
                      @click="editUser(record)">
                  <pencil-line-edit-icon height="16" width="16" stroke="#467BFF" view-box="0 0 16 16"/>
                </span>
              </a-tooltip>
              <a-tooltip>
                <template v-slot:title>
                  {{ $t('label.history') }}
                </template>
                <span class="cursor-pointer" @click="goToHistory(record)">
                  <time-reset-icon height="16" width="16" stroke="#467BFF" view-box="0 0 16 16"/>
                </span>
              </a-tooltip>
            </div>
          </template>
        </app-table>
      </template>
    </app-tabs>

    <set-user-as-admin-modal
        :show="showSetUserAsAdminModal"
        :organizations-list="organizationsList"
        @close="showSetUserAsAdminModal = false"
    />
    <user-modal :show="showUserModal" :user-slug="editableUserSlug" @close="onUserModalClose"/>
  </div>
</template>

<script>
import _ from 'lodash';
import {mapGetters} from 'vuex';
import {settings} from '@/core/defaultSettings';
import {LANGUES_SUPPORTEES} from '@/core/vue-i18n';
import generalMixin from '@/mixins/general';
import AppTable from '@/components/elements/AppTable';
import AppTabs from '@/components/elements/AppTabs';
import SetUserAsAdminModal from './modals/SetUserAsAdminModal';
import UserModal from './modals/UserModal';
import BadgesView from '@/views/badges/BadgesView';
import FormInput from '@/components/elements/FormInput';
import ArchiveBoxDownloadIcon from '@/assets/icons/archiveBoxDownloadIcon';
import CalendarIcon from '@/assets/icons/calendarIcon';
import FourCirclesIcon from '@/assets/icons/fourCirclesIcon';
import FourUsersIcon from '@/assets/icons/fourUsersIcon';
import IdentificationBadgeIdIcon from '@/assets/icons/identificationBadgeIdIcon';
import LinkIcon from '@/assets/icons/linkIcon';
import MailIcon from '@/assets/icons/mailIcon';
import PencilLineEditIcon from '@/assets/icons/pencilLineEditIcon';
import PowerIcon from '@/assets/icons/powerIcon';
import SearchIcon from '@/assets/icons/searchIcon';
import TimeResetIcon from '@/assets/icons/timeResetIcon';
import TrashIcon from '@/assets/icons/trashIcon';
import UserCircleIcon from '@/assets/icons/userCircleIcon';
import SearchableOrganizationSelect from "@/components/elements/SearchableOrganizationSelect.vue";

export default {
  components: {
    SearchableOrganizationSelect,
    AppTable,
    AppTabs,
    SetUserAsAdminModal,
    UserModal,
    BadgesView,
    FormInput,
    ArchiveBoxDownloadIcon,
    CalendarIcon,
    FourCirclesIcon,
    FourUsersIcon,
    IdentificationBadgeIdIcon,
    LinkIcon,
    MailIcon,
    PencilLineEditIcon,
    PowerIcon,
    SearchIcon,
    TimeResetIcon,
    TrashIcon,
    UserCircleIcon
  },
  mixins: [generalMixin],
  data: () => ({
    activeTab: 0,
    tabs: ['users', 'badges', 'admins'],
    showSetUserAsAdminModal: false,
    showUserModal: false,
    editableUser: null,
    filters: {
      search: null,
      organization_id: null,
      pico_taken: null
    }
  }),
  mounted() {
    this.$store.dispatch('organizationsStore/GET_DROPDOWN_LIST');
    if (this.$route.query?.organization_id) {
      this.filters.organization_id = parseInt(this.$route.query.organization_id);
    }
    if ('pico_taken' in this.$route.query) {
      this.filters.pico_taken = true;
    }
  },
  computed: {
    ...mapGetters({
      organizationsList: 'organizationsStore/DROPDOWN_LIST',
      boxData: 'usersStore/BOX_DATA',
      usersList: 'usersStore/USERS',
      usersMeta: 'usersStore/USERS_META',
      usersCount: 'usersStore/USERS_COUNT',
      adminsCount: 'usersStore/ADMINS_COUNT',
      adminsList: 'usersStore/ADMINS',
      adminsMeta: 'usersStore/ADMINS_META',
      badgesCount: 'badgesStore/BADGES_COUNT',
      userOrganizationIds: 'getOrganizationIds',
      currentOrganizationId: 'getOrganizationId'
    }),
    activeTabName() {
      return this.tabs[this.activeTab];
    },
    /* `toLocaleString` lève un RangeError sur une étiquette de langue invalide, et `$i18n.locale`
     * vient d'une valeur externe (`?lang=` → localStorage). Une étiquette pourrie faisait donc
     * exploser cette computed ET `tabCounts`, et les trois compteurs d'onglets disparaissaient
     * sans un mot côté écran — seule la console montrait « Invalid language tag: null ».
     * `undefined` demande la locale par défaut du navigateur : jamais d'exception. */
    localeSure() {
      return LANGUES_SUPPORTEES.includes(this.$i18n.locale) ? this.$i18n.locale : undefined;
    },
    formattedBadgesCount() {
      return Number(this.badgesCount || 0).toLocaleString(this.localeSure);
    },
    // Le compteur répond « où sont passés mes gens ? » avant qu'on ait à le demander.
    tabCounts() {
      const fmt = (n) => (n === null || n === undefined)
          ? ''
          : ` (${Number(n).toLocaleString(this.localeSure)})`;
      return {
        users: fmt(this.usersCount),
        badges: fmt(this.badgesCount),
        admins: fmt(this.adminsCount)
      };
    },
    // Une organisation sans aucun compte mais avec des badges : c'est le cas qui
    // dérouterait le client si la liste vide ne s'expliquait pas.
    showBadgesHint() {
      // `usersCount` et pas `usersMeta.total` : ce dernier vaut 0 avant tout chargement, ce qui
      // ferait clignoter l'encart « aucun utilisateur » sur une organisation qui en a.
      return !!this.filters.organization_id
          && this.usersCount === 0
          && this.badgesCount > 0;
    },
    userColumns() {
      return [
        {
          dataIndex: 'user_id',
          slots: {title: 'user_id_icon'},
          scopedSlots: {customRender: 'user_id'}
        },
        {
          dataIndex: 'email',
          slots: {title: 'email_icon'},
          scopedSlots: {customRender: 'email'}
        },
        {
          dataIndex: 'organization_name',
          slots: {title: 'organization_name_icon'},
          scopedSlots: {customRender: 'organization_name'}
        },
        {
          dataIndex: 'nfc_badge_id',
          slots: {title: 'nfc_badge_id_icon'},
          scopedSlots: {customRender: 'nfc_badge_id'}
        },
        {
          dataIndex: 'active_pico_cycles_count',
          slots: {title: 'active_pico_cycles_count_icon'},
          scopedSlots: {customRender: 'active_pico_cycles_count'}
        },
        {
          dataIndex: 'status',
          slots: {title: 'status_icon'},
          scopedSlots: {customRender: 'status'}
        },
        {
          title: '',
          dataIndex: 'actions',
          scopedSlots: {customRender: 'actions'}
        }
      ];
    },
    editableUserSlug() {
      return this.editableUser?.slug;
    },
    selectedOrganizationId() {
      return this.filters.organization_id;
    },
    computedLastImportDate() {
      return this.boxData?.time ? generalMixin.methods.timestampToDate(this.boxData.time) : '-';
    }
  },
  watch: {
    // Un administrateur n'appartient qu'à une organisation : lui demander de la choisir
    // dans une liste d'un seul élément n'a pas de sens, et tant qu'il n'a pas choisi les
    // compteurs restent vides. On la pré-sélectionne, la requête de route restant
    // prioritaire (elle est posée dans mounted, après ce watcher immédiat).
    userOrganizationIds: {
      immediate: true,
      handler(ids) {
        if (this.filters.organization_id) return;
        if (ids && ids.length === 1) {
          this.filters.organization_id = ids[0];
        } else if (ids && ids.length === 0 && this.currentOrganizationId) {
          this.filters.organization_id = this.currentOrganizationId;
        }
      }
    },
    selectedOrganizationId: {
      immediate: true,
      handler(id) {
        if (id) {
          this.$store.dispatch('usersStore/GET_BOX_DATA', id);
        }
        this.$store.dispatch('badgesStore/GET_BADGES_COUNT', id || null);
      }
    },
    activeTab() {
      this.getTableData();
    },
    showUserModal(value) {
      if (!value) {
        this.editableUser = null;
      }
    },
    filters: {
      immediate: true,
      handler: _.debounce(function () {
        this.getTableData();
        this.refreshTabCounts();
      }, settings.debounceTime),
      deep: true
    }
  },
  methods: {
    /* Les compteurs sont écrits dans les onglets, donc lus depuis N'IMPORTE quel onglet : ils
     * ne peuvent pas dépendre de celui qui est ouvert. On les rafraîchit avec les filtres, en
     * deux requêtes qui ne ramènent qu'un total. Sans ça, arriver sur la page affichait
     * « Admins (0) » — et un client y lit qu'il n'a plus d'administrateur. */
    refreshTabCounts() {
      const {organization_id, search} = this.filters;
      this.$store.dispatch('usersStore/GET_USERS_COUNT', {organization_id, search});
      this.$store.dispatch('usersStore/GET_ADMINS_COUNT', {organization_id, search});
    },
    getTableData(pagination) {
      const params = {...this.filters, ...pagination};
      // L'onglet Badges se charge lui-même : BadgesView réagit aux propriétés qu'on lui passe.
      if (this.activeTabName === 'badges') return;
      if (this.activeTabName === 'admins') {
        this.$store.dispatch('usersStore/GET_ADMINS', params);
      } else {
        this.$store.dispatch('usersStore/GET_USERS', params);
      }
    },
    goToHistory(user) {
      this.$router.push({name: 'History', query: {user_id: user.user_id}});
    },
    editUser(user) {
      this.editableUser = user;
      this.showUserModal = true;
    },
    onUserModalClose(e) {
      this.showUserModal = false;
      if (e) this.getTableData();
    },
    showActivateUserModal(user) {
      this.$confirm({
        title: this.$t('modal.activate_user'),
        centered: true,
        closable: true,
        okType: 'primary',
        cancelText: this.$t('btn.no'),
        okText: this.$t('btn.yes'),
        onOk: () => {
          this.$store.dispatch('usersStore/ACTIVATE_USER', user.slug)
              .then(() => {
                this.getTableData();
                this.$message.success(this.$t('alert.user_restored'));
              })
              .catch(err => this.$message.error(err));
        },
        onCancel() {
          console.log('Cancel');
        },
      });
    },
    showDestroyUserModal(user) {
      this.$confirm({
        title: this.$t('modal.delete_user'),
        centered: true,
        closable: true,
        okType: 'primary',
        cancelText: this.$t('btn.no'),
        okText: this.$t('btn.yes'),
        onOk: () => {
          this.$store.dispatch('usersStore/DESTROY_USER', user.slug)
              .then(() => {
                this.getTableData();
                this.$message.success(this.$t('alert.user_deleted'));
              })
              .catch(error => {
                this.$message.error(error.response.data?.error?.message || this.$t('alert.something_goes_wrong'));
              });
        },
        onCancel: () => {
          console.log('cancel');
        },
      });
    }
  }
}
</script>

<style lang="scss" scoped>
.gutter-example > > > .ant-row > div {
  background: transparent;
  border: 0;
}

.gutter-box {
  padding: 40px 32px;
  background: #fff;

  .gutter-box-number {
    color: #132540;
    font-size: 28px;
    font-weight: 900;
  }

  .gutter-box-label {
    color: #6886A6;
  }

  .gutter-box-btn {
    height: auto;
    margin-top: 32px;
    padding: 8px 28px;
    background: #467BFF;
  }
}

.card-container::v-deep {
  .ant-tabs {
    .ant-tabs-bar {
      margin-bottom: 0;
    }
  }
}

.hidden {
  visibility: hidden;
  opacity: 0;
}
</style>
<style lang="scss" scoped>
@media screen and (max-width: 992px) {
  .boxes {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }
}
</style>